import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../../store/hooks';
import { BatchRecordIngredient } from '../../../types/batchRecordIngredient';
import { RawIngredient } from '../../../types/rawIngredient';

interface BatchRecordIngredientModalProps {
  ingredient: BatchRecordIngredient;
  rawIngredients: RawIngredient[];
  onSave: (data: {
    rawIngredientId: string;
    lotNumbers: string[];
    quantity?: number;
    unit?: string;
  }) => void;
  onClose: () => void;
}

export const BatchRecordIngredientModal: React.FC<BatchRecordIngredientModalProps> = ({
  ingredient,
  rawIngredients,
  onSave,
  onClose
}) => {
  const traceCodes = useAppSelector(state => state.ingredientTraceCodes.traceCodes);

  // Get the ingredient name from the raw ingredient
  const initialIngredient = rawIngredients.find(ing => ing.id === ingredient.rawIngredientId);
  const initialIngredientName = initialIngredient?.name || '';
  const initialLot = ingredient.lotNumbers && ingredient.lotNumbers.length > 0 ? ingredient.lotNumbers[0] : null;

  const [selectedIngredientName, setSelectedIngredientName] = useState<string>(initialIngredientName);
  const [selectedLot, setSelectedLot] = useState<string | null>(initialLot);
  const [quantity, setQuantity] = useState<string>(
    ingredient.quantity != null ? ingredient.quantity.toString() : ''
  );
  const [unit, setUnit] = useState<string>(ingredient.unit || '');

  // Helper function to get unique ingredient names
  const getUniqueIngredientNames = (): string[] => {
    const names = new Set(rawIngredients.map(ing => ing.name));
    return Array.from(names).sort();
  };

  // Helper function to determine rawIngredientId from selected lot
  const getRawIngredientIdForLot = (lotNumber: string): string | null => {
    // Find the trace code
    const traceCode = traceCodes.find(tc => tc.internalTraceCode === lotNumber);
    if (!traceCode) return null;

    // Find the raw ingredient with matching receivingLogId
    const rawIngredient = rawIngredients.find(
      ing => ing.receivingLogId === traceCode.ingredientReceivingId
    );

    return rawIngredient?.id || null;
  };

  // Get available lots for the selected ingredient name
  const availableLots = (): string[] => {
    if (!selectedIngredientName) return [];

    // Find all raw ingredients with this name
    const matchingIngredients = rawIngredients.filter(ing => ing.name === selectedIngredientName);

    if (matchingIngredients.length === 0) return [];

    // Get all receiving log IDs
    const receivingLogIds = matchingIngredients.map(ing => ing.receivingLogId);

    // Get all trace codes for these receiving logs
    const ingredientTraceCodes = traceCodes.filter(
      tc => receivingLogIds.includes(tc.ingredientReceivingId || '')
    );

    // Return unique internal trace codes
    return Array.from(new Set(ingredientTraceCodes.map(tc => tc.internalTraceCode)));
  };

  // Reset selected lot when ingredient name changes
  useEffect(() => {
    if (selectedIngredientName !== initialIngredientName) {
      setSelectedLot(null);
    }
  }, [selectedIngredientName, initialIngredientName]);

  const handleSave = () => {
    if (!selectedIngredientName || !selectedLot) {
      alert('Please select an ingredient and lot number');
      return;
    }

    // Validate quantity format if provided
    let quantityNum: number | undefined = undefined;
    if (quantity.trim() !== '') {
      quantityNum = parseFloat(quantity);
      if (isNaN(quantityNum) || quantityNum <= 0) {
        alert('Please enter a valid quantity greater than 0, or leave it empty');
        return;
      }
    }

    // Determine the rawIngredientId from the selected lot
    const rawIngredientId = getRawIngredientIdForLot(selectedLot);
    if (!rawIngredientId) {
      alert('Could not determine ingredient. Please try again.');
      return;
    }

    onSave({
      rawIngredientId,
      lotNumbers: [selectedLot], // Array with single element
      quantity: quantityNum,
      unit: unit.trim() !== '' ? unit : undefined
    });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}
        >
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#333' }}>
            Edit Ingredient
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            marginBottom: '24px'
          }}
        >
          {/* Ingredient Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#555',
                fontSize: '14px'
              }}
            >
              Ingredient Name
            </label>
            <select
              value={selectedIngredientName}
              onChange={(e) => {
                setSelectedIngredientName(e.target.value);
                setSelectedLot(null);
              }}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              {getUniqueIngredientNames().map(name => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* Lot Number Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#555',
                fontSize: '14px'
              }}
            >
              Lot Number
            </label>
            {availableLots().length > 0 ? (
              <div
                style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '12px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  backgroundColor: '#fafafa'
                }}
              >
                {availableLots().map(lot => (
                  <label
                    key={lot}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      marginBottom: '4px',
                      backgroundColor: selectedLot === lot ? '#e3f2fd' : 'transparent',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedLot !== lot) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedLot !== lot) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <input
                      type="radio"
                      name="lotSelection"
                      checked={selectedLot === lot}
                      onChange={() => setSelectedLot(lot)}
                      style={{
                        marginRight: '8px',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ fontSize: '14px', color: '#333', fontFamily: 'monospace' }}>{lot}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div
                style={{
                  padding: '12px',
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                No lots available for this ingredient
              </div>
            )}
          </div>

          {/* Quantity */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#555',
                fontSize: '14px'
              }}
            >
              Quantity (optional)
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              step="0.01"
              min="0"
              placeholder="Leave empty if not measured"
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Unit */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '600',
                color: '#555',
                fontSize: '14px'
              }}
            >
              Unit (optional)
            </label>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Not specified</option>
              <option value="kg">kg</option>
              <option value="g">g</option>
              <option value="lb">lb</option>
              <option value="oz">oz</option>
              <option value="L">L</option>
              <option value="mL">mL</option>
              <option value="gal">gal</option>
              <option value="qt">qt</option>
              <option value="pt">pt</option>
              <option value="cup">cup</option>
              <option value="tbsp">tbsp</option>
              <option value="tsp">tsp</option>
              <option value="unit">unit</option>
            </select>
          </div>
        </div>

        {/* Footer Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            paddingTop: '16px',
            borderTop: '1px solid #e0e0e0'
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              backgroundColor: '#757575',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#616161';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#757575';
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#45a049';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#4CAF50';
            }}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
